import { useGetEligiblePlans } from './hooks';
import { PlansList, deduplicatePlans } from './PlansList';
import { InvalidLoanAmountState } from './InvalidLoanAmountState';
import { NoPlansAvailableState } from './NoPlansAvailableState';
import { PreApprovalSection } from './PreApprovalSection';
import { usePreApprovalDisplay } from './usePreApprovalDisplay';
import { GreenSkyPreApprovalDisclosure } from '../disclosures';
import { Calculation, ErrorInfo } from '@servicetitan/financing-api-client';
import { SetupDataStore, useProviderConfig } from '@servicetitan/financing-workflow-shared';
import { LoadingScreen } from '@servicetitan/financing-utils-shared';
import { useLoanAmountValidation } from '../providers/useLoanAmountValidation';
import { FinancedAmountStore } from '../financing-information/financedAmountStore';
import { CustomPlansStore } from './CustomPlansStore';

declare module '@tanstack/react-query' {
    interface Register {
        defaultError: ErrorInfo;
    }
}

const SELECTOR_TITLE_DEFAULT = 'Select a Plan';
const SELECTOR_TITLE_OVERRIDE = 'Not seeing what you want? Check eligibility for other plans';

interface ContainerProps {
    onPlanSelected?: (plan: Calculation) => void;
}

export const Container = ({ onPlanSelected }: ContainerProps) => {
    const { financedAmount } = FinancedAmountStore.useStore();
    const { setupData, setSetupData } = SetupDataStore.useStore();
    const {
        customPlanIds,
        hasCustomPlans,
        provider: providerFromUrl,
    } = CustomPlansStore.useStore();
    const { planSelectionInstructions } = useProviderConfig();
    // When the currently selected provider is the same as the provider from the URL, use the custom plans
    const useCustomPlans = providerFromUrl === setupData?.provider && hasCustomPlans;

    const { data, isLoading, isError } = useGetEligiblePlans({
        providerName: setupData?.provider ?? '',
        tenantId: setupData?.tenantId ?? null,
        buId: setupData?.buId ?? null,
        amount: financedAmount!,
        allPlans: true,
        state: setupData?.state,
        useAdditionalPromotions: true,
        planList: useCustomPlans ? customPlanIds : null,
        // For FinanceIt, always request all payment frequencies to get comprehensive plan data
        paymentFrequency: setupData?.provider?.toLowerCase() === 'financeit' ? 'all' : null,
        merchantId: 'all',
    });

    const { noPlansEligible, minimumAmount } = data ?? {};
    const validation = useLoanAmountValidation(financedAmount);
    const display = usePreApprovalDisplay();

    const plans = data?.data ?? [];
    // A failed/absent list query (the eligible-plans query is also disabled for an out-of-bounds
    // amount, reporting no data) renders blank, exactly as today.
    const listIsError = isError || (!isLoading && data == null);

    const isPreApprovalShown = display.kind === 'visible' || display.kind === 'outOfRange';

    // The page-level loading / invalid-amount / no-plans notices only apply with no pre-approval to
    // show: a visible/loading pre-approval resolves on its own clock and suppresses them so the card
    // can render even when the BU-rule list is empty. The order matters — validation must win over
    // listIsError, since an out-of-bounds amount disables the list query (no data) yet must still
    // show the "between …" notice rather than a blank page.
    if (display.kind === 'hidden') {
        if (isLoading) {
            return <LoadingScreen message="Loading available plans..." />;
        }
        if (!validation.isValidLoanAmount) {
            return (
                <InvalidLoanAmountState
                    minimumLoanAmount={validation.minAmount}
                    maximumLoanAmount={validation.maxAmount}
                />
            );
        }
        if (listIsError) {
            return null;
        }
        if (plans.length === 0 && noPlansEligible) {
            return <InvalidLoanAmountState minimumLoanAmount={minimumAmount!} />;
        }
        if (plans.length === 0) {
            return <NoPlansAvailableState />;
        }
    }

    // Regular cards clear preApprovalId before delegating; the pre-approval card sets it. preApprovalId
    // is the single source of truth for the hard credit application — useProviderConfig and the
    // submission/refresh hooks key the GreenSky hard/soft decision off it — so neither path touches
    // isApplyingForPrequalification. Leaving the soft flag alone is load-bearing: a customer who
    // started in the soft prequal flow, briefly picked the pre-approval, then picked a regular plan
    // returns to the soft app instead of being stranded in the hard config. Both use a functional
    // merge so other setup fields (provider/tenantId/etc.) survive.
    const selectRegular = (plan: Calculation) => {
        // Only write the store when there's a pre-approval anchor to clear. Returning the previous
        // reference makes React bail out of the update, so the non-pre-approval Container consumers
        // (update-offer, authorize/change-order edit-plan) don't re-render every SetupDataStore
        // subscriber on a plain plan pick.
        setSetupData(prev => (prev?.preApprovalId ? { ...prev, preApprovalId: undefined } : prev));
        onPlanSelected?.(plan);
    };
    const preApprovalId = isPreApprovalShown ? (display.preApproval.id ?? undefined) : undefined;
    const selectPreApproval = (plan: Calculation) => {
        setSetupData(prev => ({ ...prev!, preApprovalId }));
        onPlanSelected?.(plan);
    };

    // Exclude the pre-approved plan from the selector so it isn't rendered twice (and so picking it
    // there can't route it to the soft app via selectRegular). This applies for every non-hidden
    // state — including `loading`, before the card resolves: otherwise a quick pick of that plan
    // during the calc would clear preApprovalId and start the soft prequal instead of the hard credit
    // application, and the plan would briefly list in both the selector and the resolving card.
    // Pre-approval is GreenSky-only, where merchant/frequency tabs are off, so the deduplicated set
    // equals the selector's displayed set — hasOtherPlans here matches what PlansList renders.
    const preApprovedPlanNumber =
        display.kind === 'hidden' ? undefined : display.preApproval.planNumber;
    const selectablePlans =
        preApprovedPlanNumber != null
            ? deduplicatePlans(plans).filter(p => p.planNumber !== preApprovedPlanNumber)
            : plans;
    const hasOtherPlans = selectablePlans.length > 0;

    // The card area is a placeholder while the targeted calc is in flight, the pre-approval section
    // once it resolves, and nothing otherwise. It is the selector's leading content so both share
    // the `.plans-container` container-query context.
    const leadingContent = isPreApprovalShown ? (
        <PreApprovalSection
            display={display}
            hasOtherPlans={hasOtherPlans}
            onPlanSelected={selectPreApproval}
        />
    ) : display.kind === 'loading' ? (
        <LoadingScreen message="Checking your pre-approval..." />
    ) : undefined;

    return (
        <PlansList
            plans={selectablePlans}
            instructions={planSelectionInstructions}
            onPlanSelected={selectRegular}
            title={isPreApprovalShown ? SELECTOR_TITLE_OVERRIDE : SELECTOR_TITLE_DEFAULT}
            // On visible/outOfRange with no other plans, the selector (and its empty/no-plans notice)
            // is suppressed entirely — the pre-approval section is the page body.
            showSelector={!(isPreApprovalShown && !hasOtherPlans)}
            leadingContent={leadingContent}
            leadingDisclosure={
                isPreApprovalShown ? (
                    <GreenSkyPreApprovalDisclosure text={display.generalDisclosure} />
                ) : undefined
            }
        />
    );
};
